import { useEffect, useState, type FormEvent } from 'react';
import { systemRoles, type RoleSummary } from './system.api';
import {
  ErrorNote, Field, inputClass, buttonClass, secondaryButtonClass, tableHeadClass,
} from './SystemLayout';

/**
 * One organisation's roles.
 *
 * THE HONEST CAVEAT, shown on the screen rather than buried here: this
 * schema has no permissions table, and `requirePermissions()` in the API
 * is a documented pass-through. The only thing the code branches on is
 * `system_key` (SUPER_ADMIN / ADMIN), which cannot be set from here.
 *
 * So a role created here is a LABEL - it identifies someone in People and
 * in reports, exactly as Consultant does - and grants nothing. Saying so
 * in the UI costs one sentence and saves somebody building an access model
 * out of rows that do not enforce anything.
 */
export function RolesPanel({ orgId, orgName }: { orgId: string; orgName: string }) {
  const [rows, setRows] = useState<RoleSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const result = await systemRoles.list(orgId);
        if (!cancelled) setRows(result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load roles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  async function remove(role: RoleSummary) {
    if (!window.confirm(`Remove the role "${role.name}" from ${orgName}?`)) return;
    setError(null);
    try {
      setRows(await systemRoles.remove(orgId, role.id));
    } catch (err) {
      // The API refuses a built-in, and one that somebody still holds.
      setError(err instanceof Error ? err.message : 'Could not remove the role');
    }
  }

  return (
    <div>
      <ErrorNote message={error} />

      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Roles are labels: they identify people in People and in reports. Permissions
          are not yet modelled in this system, so a role grants nothing on its own.
        </p>
        <button type="button" onClick={() => { setAdding((v) => !v); setEditing(null); }}
                className={secondaryButtonClass}>
          {adding ? 'Cancel' : 'Add role'}
        </button>
      </div>

      {adding && (
        <RoleForm
          submit={(input) => systemRoles.create(orgId, { name: input.name, description: input.description })}
          onSaved={(next) => { setRows(next); setAdding(false); }}
          onError={setError}
        />
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className={tableHeadClass}>
              <th className="py-2 font-medium">Role</th>
              <th className="py-2 font-medium">Holders</th>
              <th className="py-2 font-medium">Kind</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((role) => (
              <tr key={role.id} className="border-b border-slate-100 align-top last:border-0">
                <td className="py-2.5">
                  {editing === role.id ? (
                    <RoleForm
                      initial={role}
                      submit={(input) => systemRoles.update(orgId, role.id, input)}
                      onSaved={(next) => { setRows(next); setEditing(null); }}
                      onError={setError}
                      onCancel={() => setEditing(null)}
                    />
                  ) : (
                    <>
                      <span className="font-medium text-slate-900">{role.name}</span>
                      {role.isDefaultForNewUsers && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                          default
                        </span>
                      )}
                      {role.description && (
                        <span className="block text-xs text-slate-500">{role.description}</span>
                      )}
                    </>
                  )}
                </td>
                <td className="py-2.5 text-slate-500">{role.userCount}</td>
                <td className="py-2.5 text-slate-500">
                  {role.isBuiltIn
                    ? <span title={`system_key: ${role.systemKey ?? 'none'}`}>Built-in</span>
                    : 'Custom'}
                </td>
                <td className="py-2.5 text-right">
                  {editing !== role.id && (
                    <>
                      <button type="button" onClick={() => { setEditing(role.id); setAdding(false); }}
                        className="text-xs font-medium text-brand-600 underline-offset-2 hover:underline">
                        Rename
                      </button>
                      {/* Built-ins are not offered for removal: the code
                          branches on system_key, and an organisation with no
                          Super Admin role cannot be administered at all. */}
                      {!role.isBuiltIn && (
                        <button type="button" onClick={() => void remove(role)}
                          className="ml-3 text-xs font-medium text-red-600 underline-offset-2 hover:underline">
                          Remove
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RoleForm({ initial, submit, onSaved, onError, onCancel }: {
  initial?: RoleSummary;
  submit: (input: { name: string; description: string | null }) => Promise<RoleSummary[]>;
  onSaved: (rows: RoleSummary[]) => void;
  onError: (message: string | null) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [busy, setBusy] = useState(false);

  async function handle(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    onError(null);
    try {
      onSaved(await submit({ name, description: description.trim() || null }));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save the role');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void handle(e)}
          className="mb-4 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_1.5fr_auto]">
      <Field label="Name">
        <input required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
      </Field>
      <Field label="Description">
        <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
      </Field>
      <div className="flex items-end gap-2">
        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Saving...' : 'Save'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        )}
      </div>
    </form>
  );
}
